import React, { useRef, useContext } from "react";
import PropTypes from "prop-types";
import { ConfigContext } from "../../Config/ConfigContext";
import { useMicrosoftOAuth } from "./hooks/useMicrosoftOAuth";
import { PublicClientApplication } from "@azure/msal-browser";
import Button from "@mui/material/Button";

/**
 * MicrosoftLogin component handles user login via Microsoft authentication.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {function} props.onSuccess - Callback function executed on successful authentication.
 * @param {function} props.onError - Callback function executed when an error occurs during authentication.
 * @returns {JSX.Element} The rendered Microsoft login button.
 */
export default function MicrosoftLogin({ onSuccess, onError }) {
	const btnContainerRef = useRef(null);
	const { clientId } = useMicrosoftOAuth();
	const { config } = useContext(ConfigContext);

	const onSuccessRef = useRef(onSuccess);
	onSuccessRef.current = onSuccess;

	const onErrorRef = useRef(onError);
	onErrorRef.current = onError;

	const getMsalInstance = async () => {
		const msalConfig = {
			auth: {
				clientId,
				authority: "https://login.microsoftonline.com/" + config.microsoftOAuth.tenantId
			}
		};

		try {
			const msalInstance = await PublicClientApplication.createPublicClientApplication(
				msalConfig
			);
			const msalResponse = await msalInstance.loginPopup({
				prompt: "select_account"
			});
			onSuccessRef.current(msalResponse);
		} catch (error) {
			onErrorRef.current(error);
		}
	};

	return (
		<Button
			ref={btnContainerRef}
			sx={{
				borderRadius: "90px",
				border: "1px solid #5b626E",
				color: "#1B1C1E",
				textTransform: "initial",
				padding: "8px",
				display: "flex",
				alignItems: "center",
				gap: "8px",
				fontWeight: "700",
				fontSize: "15px",
				width: "100%"
			}}
			onClick={() => {
				getMsalInstance();
			}}
		>
			Continue with Microsoft
			<svg xmlns="http://www.w3.org/2000/svg" width="25px" viewBox="0 0 23 23">
				<path fill="#f3f3f3" d="M0 0h23v23H0z" />
				<path fill="#f35325" d="M1 1h10v10H1z" />
				<path fill="#81bc06" d="M12 1h10v10H12z" />
				<path fill="#05a6f0" d="M1 12h10v10H1z" />
				<path fill="#ffba08" d="M12 12h10v10H12z" />
			</svg>
		</Button>
	);
}

MicrosoftLogin.propTypes = {
	onSuccess: PropTypes.func.isRequired,
	onError: PropTypes.func.isRequired
};
